import { onValue, ref } from 'firebase/database';
import { useEffect, useState } from 'react';
import { database } from '../firebase';

const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const TYPE_COLORS = {
  holiday:  { bg: '#fff3cd', border: '#ffc107', text: '#856404', badge: '#ffc107' },
  festival: { bg: '#fde8f0', border: '#e91e8c', text: '#9c1060', badge: '#e91e8c' },
  national: { bg: '#d4edda', border: '#28a745', text: '#155724', badge: '#28a745' },
  event:    { bg: '#d1ecf1', border: '#17a2b8', text: '#0c5460', badge: '#17a2b8' },
};

const PublicCalendar = () => {
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState({});

  useEffect(() => {
    const unsub = onValue(ref(database, 'calendar_events'), (snap) => {
      const raw = snap.val() || {};
      const grouped = {};
      Object.entries(raw).forEach(([id, v]) => {
        if (!grouped[v.date]) grouped[v.date] = [];
        grouped[v.date].push({ id, ...v });
      });
      setEvents(grouped);
    });
    return () => unsub();
  }, []);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y-1); } else setMonth(m => m-1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y+1); } else setMonth(m => m+1); };
  const goToday   = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const daysInPrev  = new Date(year, month, 0).getDate();
  const pad = (n) => String(n).padStart(2,'0');
  const dateKey = (y,m,d) => `${y}-${pad(m+1)}-${pad(d)}`;

  const cells = [];
  for (let i=0; i<firstDay; i++) cells.push({ day: daysInPrev-firstDay+1+i, cur: false });
  for (let i=1; i<=daysInMonth; i++) cells.push({ day: i, cur: true });
  const remaining = 42 - cells.length;
  for (let i=1; i<=remaining; i++) cells.push({ day: i, cur: false });

  const isToday = (cell) => cell.cur && year===today.getFullYear() && month===today.getMonth() && cell.day===today.getDate();

  const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate());
  const upcoming = [];
  Object.entries(events).forEach(([date, evts]) => {
    if (date >= todayKey) evts.forEach(e => upcoming.push({ date, ...e }));
  });
  upcoming.sort((a,b) => a.date.localeCompare(b.date));

  return (
    <>
      <style>{`
        .pcal-hero {
          background: linear-gradient(135deg, #4d3d6c 0%, #6b4f9e 60%, #90b732 100%);
          padding: 48px 24px 40px;
          color: #fff;
          position: relative; overflow: hidden;
        }
        .pcal-hero::before {
          content:''; position:absolute; top:-60px; right:-60px;
          width:260px; height:260px;
          background:rgba(144,183,50,0.15); border-radius:50%;
        }
        .pcal-hero h1 { font-size: clamp(1.6rem,4vw,2.4rem); font-weight:800; }
        .pcal-hero p  { font-size:14px; opacity:0.85; max-width:480px; }
        .pcal-hero-badge {
          display:inline-block;
          background:rgba(255,255,255,0.15);
          border:1px solid rgba(255,255,255,0.3);
          border-radius:50px; padding:4px 16px;
          font-size:12px; margin-bottom:12px;
        }

        .pcal-wrap { padding: 32px 0 64px; background: #f8fdf0; }

        .pcal-header {
          background: #4d3d6c; color:#fff;
          border-radius:12px 12px 0 0;
          padding:12px 20px;
          display:flex; align-items:center; justify-content:space-between;
        }
        .pcal-header h5 { margin:0; font-weight:700; font-size:16px; }
        .pcal-nav-btn {
          background:rgba(255,255,255,0.15); border:none; color:#fff;
          border-radius:6px; padding:4px 12px; cursor:pointer; font-size:15px;
          transition:background 0.2s;
        }
        .pcal-nav-btn:hover { background:rgba(255,255,255,0.3); }
        .pcal-today-btn {
          background:#90b732; border:none; color:#fff;
          border-radius:6px; padding:4px 14px;
          cursor:pointer; font-size:12px; font-weight:600;
        }
        .pcal-today-btn:hover { background:#6fa020; }

        .pcal-grid { border:1px solid #e0e0e0; border-top:none; border-radius:0 0 12px 12px; overflow:hidden; background:#fff; }
        .pcal-days-header { display:grid; grid-template-columns:repeat(7,1fr); background:#f8fdf0; }
        .pcal-day-name { text-align:center; padding:8px 0; font-weight:700; font-size:12px; color:#4d3d6c; border-right:1px solid #eee; }
        .pcal-day-name:last-child { border-right:none; }
        .pcal-cells { display:grid; grid-template-columns:repeat(7,1fr); }
        .pcal-cell {
          min-height:80px; padding:6px;
          border-right:1px solid #eee; border-bottom:1px solid #eee;
        }
        .pcal-cell:nth-child(7n) { border-right:none; }
        .pcal-cell.other-month { background:#fafafa; }
        .pcal-cell.is-today { background:#ecf3d7; }
        .pcal-cell.is-today .pcal-date-num {
          background:#90b732; color:#fff;
          border-radius:50%; width:24px; height:24px;
          display:flex; align-items:center; justify-content:center;
        }
        .pcal-date-num { font-size:13px; font-weight:600; color:#333; margin-bottom:3px; display:inline-block; }
        .pcal-event-pill {
          border-radius:4px; padding:2px 6px; margin-bottom:2px;
          font-size:10px; font-weight:600; line-height:1.3;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
        }

        /* Upcoming */
        .pcal-upcoming { border:1px solid #e0e0e0; border-radius:12px; overflow:hidden; }
        .pcal-upcoming-header { background:#4d3d6c; color:#fff; padding:12px 16px; font-weight:700; font-size:14px; }
        .pcal-upcoming-item {
          display:flex; align-items:center; gap:12px;
          padding:10px 16px; border-bottom:1px solid #f0f0f0;
        }
        .pcal-upcoming-item:last-child { border-bottom:none; }
        .pcal-date-box {
          min-width:44px; text-align:center;
          background:#ecf3d7; border-radius:8px; padding:4px 6px;
        }
        .pcal-date-box .ud { font-size:18px; font-weight:800; color:#4d3d6c; line-height:1; }
        .pcal-date-box .um { font-size:10px; color:#90b732; font-weight:700; text-transform:uppercase; }
        .pcal-event-name { font-weight:600; color:#2d2d2d; font-size:13px; }
        .pcal-type-badge { font-size:10px; font-weight:700; border-radius:50px; padding:2px 8px; color:#fff; display:inline-block; margin-top:2px; }

        .pcal-legend { display:flex; gap:14px; flex-wrap:wrap; margin-top:10px; }
        .pcal-legend-item { display:flex; align-items:center; gap:5px; font-size:12px; color:#555; }
        .pcal-legend-dot { width:10px; height:10px; border-radius:3px; }
      `}</style>

      {/* Hero */}
      <div className="pcal-hero">
        <div className="container">
          <span className="pcal-hero-badge">📅 Academic Calendar {year}</span>
          <h1>Holiday & Event Calendar</h1>
          <p className="mt-2">View upcoming holidays, festivals, and institute events. Stay informed about all planned non-working days.</p>
        </div>
      </div>

      <div className="pcal-wrap">
        <div className="container">
          <div className="row g-4">

            {/* Calendar */}
            <div className="col-lg-8">
              <div className="pcal-header">
                <div className="d-flex gap-2">
                  <button className="pcal-nav-btn" onClick={prevMonth}>‹</button>
                  <button className="pcal-nav-btn" onClick={nextMonth}>›</button>
                </div>
                <h5>{MONTHS[month]} {year}</h5>
                <button className="pcal-today-btn" onClick={goToday}>Today</button>
              </div>

              <div className="pcal-grid">
                <div className="pcal-days-header">
                  {DAYS.map(d => <div key={d} className="pcal-day-name">{d}</div>)}
                </div>
                <div className="pcal-cells">
                  {cells.map((cell, i) => {
                    const key  = cell.cur ? dateKey(year, month, cell.day) : null;
                    const evts = key ? (events[key] || []) : [];
                    const isSun = i % 7 === 0;
                    return (
                      <div key={i} className={`pcal-cell ${!cell.cur ? 'other-month' : ''} ${isToday(cell) ? 'is-today' : ''}`}>
                        <span className="pcal-date-num" style={isSun && cell.cur && !isToday(cell) ? { color:'#e74c3c' } : {}}>
                          {cell.day}
                        </span>
                        {evts.map(ev => {
                          const c = TYPE_COLORS[ev.type] || TYPE_COLORS.event;
                          return (
                            <div key={ev.id} className="pcal-event-pill" style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
                              {ev.name}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div className="pcal-legend">
                {Object.entries(TYPE_COLORS).map(([type, c]) => (
                  <div key={type} className="pcal-legend-item">
                    <div className="pcal-legend-dot" style={{ background: c.badge }}></div>
                    <span className="text-capitalize">{type}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming */}
            <div className="col-lg-4">
              <div className="pcal-upcoming">
                <div className="pcal-upcoming-header">
                  <i className="bi bi-calendar-event me-2"></i>Upcoming Events
                </div>
                <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                  {upcoming.length === 0 ? (
                    <div className="text-center text-muted py-4" style={{ fontSize:13 }}>No upcoming events</div>
                  ) : upcoming.slice(0,25).map((ev, i) => {
                    const d = new Date(ev.date + 'T00:00:00');
                    const c = TYPE_COLORS[ev.type] || TYPE_COLORS.event;
                    return (
                      <div key={i} className="pcal-upcoming-item">
                        <div className="pcal-date-box">
                          <div className="ud">{d.getDate()}</div>
                          <div className="um">{MONTHS[d.getMonth()].slice(0,3)}</div>
                        </div>
                        <div>
                          <div className="pcal-event-name">{ev.name}</div>
                          <span className="pcal-type-badge" style={{ background: c.badge }}>{ev.type}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default PublicCalendar;
